import React, { useState } from "react";
import {
  fireEvent,
  render,
  screen,
  waitFor,
  within,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { FilterRailLayout } from "./FilterRail";
import type { FilterInterface } from "./SearchFilters";
import type { FilterValues } from "./filterState";

// Stands in for the viewport: the rail shows from lg up, the drawer below.
let mockWide = true;
jest.mock("../hooks/useWideLayout", () => ({
  useWideLayout: () => mockWide,
}));

const COLOUR = "value.look.colour.value";
const SHAPE = "value.look.shape.value";
const schema: FilterInterface = {
  path: "value",
  children: [
    {
      path: "value.look",
      children: [
        { path: COLOUR, type: "string", allowed_values: ["Red", "Green"] },
        { path: SHAPE, type: "string", allowed_values: ["Round", "Square"] },
      ],
    },
  ],
};
const green: FilterValues = {
  [COLOUR]: { path: COLOUR, type: "string", allowed_values: ["Green"] },
};

/** Holds filter state the way the search views do. */
const Harness = ({
  initial = {},
  withSchema = true,
  schemaLoading = false,
  tutorialRef,
  onValues = () => {},
  primaryFacets,
  resultsLoading,
}: {
  resultsLoading?: boolean;
  initial?: FilterValues;
  withSchema?: boolean;
  schemaLoading?: boolean;
  tutorialRef?: React.RefCallback<HTMLElement>;
  onValues?: (values: FilterValues) => void;
  primaryFacets?: readonly string[];
}) => {
  const [values, setValues] = useState<FilterValues>(initial);
  return (
    <FilterRailLayout
      schema={withSchema ? schema : undefined}
      schemaLoading={schemaLoading}
      value={values}
      onChange={(next) => {
        setValues(next);
        onValues(next);
      }}
      primaryFacets={primaryFacets}
      resultCount={3}
      resultsLoading={resultsLoading}
      tutorialRef={tutorialRef}
    >
      <p>the results</p>
    </FilterRailLayout>
  );
};

const openRail = () =>
  fireEvent.click(screen.getByRole("button", { name: /^Filters/ }));

describe("FilterRailLayout on a wide viewport", () => {
  beforeEach(() => {
    mockWide = true;
  });

  it("starts collapsed, and opens the filters in a rail beside the results", () => {
    render(<Harness />);
    expect(screen.queryByRole("complementary")).not.toBeInTheDocument();

    openRail();

    const rail = screen.getByRole("complementary", { name: "Filters" });
    expect(
      within(rail).getByRole("button", { name: "Look" }),
    ).toBeInTheDocument();
    expect(screen.getByText("the results")).toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "Filters" }),
    ).not.toBeInTheDocument();
  });

  it("starts open when the page loads with filters set", () => {
    render(<Harness initial={green} />);

    expect(
      screen.getByRole("complementary", { name: /^Filters/ }),
    ).toBeInTheDocument();
  });

  it("counts the active filters in the rail heading", () => {
    render(<Harness initial={green} />);

    expect(
      screen.getByRole("heading", { name: /^Filters.*1 active/ }),
    ).toBeInTheDocument();
  });

  it("hides the rail and brings it back with its groups as they were", () => {
    render(<Harness />);
    openRail();
    fireEvent.click(screen.getByRole("button", { name: "Look" }));

    fireEvent.click(screen.getByRole("button", { name: "Hide filters" }));
    expect(screen.queryByRole("complementary")).not.toBeInTheDocument();
    expect(screen.getByText("the results")).toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: "Filters" }));
    expect(screen.getByRole("button", { name: "Look" })).toHaveAttribute(
      "aria-expanded",
      "true",
    );
  });

  it("shows the primary facets first and the rest under More filters", () => {
    render(<Harness primaryFacets={[COLOUR]} />);
    openRail();

    const rail = screen.getByRole("complementary", { name: "Filters" });
    expect(
      within(rail).getByRole("group", { name: "Colour" }),
    ).toBeInTheDocument();
    fireEvent.click(within(rail).getByRole("button", { name: "More filters" }));
    expect(
      within(rail).getByRole("button", { name: "Look" }),
    ).toBeInTheDocument();
  });

  it("finds a filter from the search box at the top of the rail", () => {
    render(<Harness />);
    openRail();
    const rail = screen.getByRole("complementary", { name: "Filters" });

    fireEvent.change(within(rail).getByRole("searchbox"), {
      target: { value: "square" },
    });

    expect(within(rail).getByText("Look · Has “Square”")).toBeVisible();
    // Shape's two options, as checkboxes.
    expect(within(rail).getAllByRole("checkbox")).toHaveLength(2);

    fireEvent.click(
      within(rail).getByRole("button", { name: "Clear filter search" }),
    );
    expect(within(rail).getByRole("searchbox")).toHaveValue("");
    expect(within(rail).getByRole("searchbox")).toHaveFocus();
    expect(
      within(rail).getByRole("button", { name: "Look" }),
    ).toBeInTheDocument();
  });

  it("says the filters are loading until the schema arrives", () => {
    render(<Harness withSchema={false} schemaLoading />);
    openRail();

    const rail = screen.getByRole("complementary", { name: "Filters" });
    expect(within(rail).getByText("Loading filters…")).toBeInTheDocument();
    expect(within(rail).queryByRole("searchbox")).not.toBeInTheDocument();
  });

  it("offers no filters when there is no schema to show", () => {
    render(<Harness withSchema={false} />);

    expect(screen.queryByRole("complementary")).not.toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: /Filters/ }),
    ).not.toBeInTheDocument();
    expect(screen.getByText("the results")).toBeInTheDocument();
  });

  it("anchors the tutorial on the rail heading, and on the button while hidden", () => {
    const tutorialRef = jest.fn();
    render(<Harness tutorialRef={tutorialRef} />);
    expect(tutorialRef).toHaveBeenLastCalledWith(
      screen.getByRole("button", { name: "Filters" }),
    );

    openRail();
    expect(tutorialRef).toHaveBeenLastCalledWith(
      screen.getByRole("heading", { name: "Filters" }),
    );

    fireEvent.click(screen.getByRole("button", { name: "Hide filters" }));
    expect(tutorialRef).toHaveBeenLastCalledWith(
      screen.getByRole("button", { name: "Filters" }),
    );
  });
});

describe("FilterRailLayout on a narrow viewport", () => {
  beforeEach(() => {
    mockWide = false;
  });

  it("puts the filters in a drawer behind a Filters button", () => {
    render(<Harness initial={green} />);
    expect(screen.queryByRole("complementary")).not.toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: /^Filters.*1 active/ }));

    const drawer = screen.getByRole("dialog", { name: /^Filters/ });
    // The group holding the active filter opens on its own.
    expect(
      within(drawer).getByRole("button", { name: /^Look/ }),
    ).toHaveAttribute("aria-expanded", "true");
  });

  it("closes the drawer from its Show results button", async () => {
    render(<Harness />);
    fireEvent.click(screen.getByRole("button", { name: "Filters" }));

    fireEvent.click(screen.getByRole("button", { name: "Show 3 results" }));

    await waitFor(() =>
      expect(screen.queryByRole("dialog")).not.toBeInTheDocument(),
    );
  });

  it("says the results are updating instead of showing a stale count", () => {
    const { rerender } = render(<Harness resultsLoading />);
    fireEvent.click(screen.getByRole("button", { name: "Filters" }));

    expect(
      screen.getByRole("button", { name: "Updating results…" }),
    ).toBeInTheDocument();

    rerender(<Harness resultsLoading={false} />);
    expect(
      screen.getByRole("button", { name: "Show 3 results" }),
    ).toBeInTheDocument();
  });

  it("clears the search on Escape before closing the drawer", async () => {
    render(<Harness />);
    fireEvent.click(screen.getByRole("button", { name: "Filters" }));
    const search = within(screen.getByRole("dialog")).getByRole("searchbox");
    fireEvent.change(search, { target: { value: "round" } });

    fireEvent.keyDown(search, { key: "Escape" });
    expect(search).toHaveValue("");
    expect(screen.getByRole("dialog")).toBeInTheDocument();

    fireEvent.keyDown(search, { key: "Escape" });
    await waitFor(() =>
      expect(screen.queryByRole("dialog")).not.toBeInTheDocument(),
    );
  });

  it("clears every filter from the drawer", () => {
    const onValues = jest.fn();
    render(<Harness initial={green} onValues={onValues} />);
    fireEvent.click(screen.getByRole("button", { name: /^Filters/ }));

    fireEvent.click(screen.getByRole("button", { name: "Clear all" }));

    expect(onValues).toHaveBeenLastCalledWith({});
    expect(screen.getByRole("button", { name: "Clear all" })).toBeDisabled();
  });

  it("anchors the tutorial on the Filters button", () => {
    const tutorialRef = jest.fn();
    render(<Harness tutorialRef={tutorialRef} />);

    expect(tutorialRef).toHaveBeenLastCalledWith(
      screen.getByRole("button", { name: "Filters" }),
    );
  });
});
